// === API Client — authenticated fetch helper ===
//
// THE STANDARD for all company-data hooks in the web app.
//
// WHY THIS EXISTS:
//   All data that belongs to a company must go through the API worker, not
//   directly to Supabase. The API enforces authorisation, validation, and
//   business rules. Supabase RLS alone is not sufficient.
//
// USAGE in a hook:
//   import { apiFetch } from '../lib/api-client';
//
//   const res = await apiFetch('/api/v1/general-tasks?page=1&per_page=10');
//   if (!res.ok) throw new Error(`HTTP ${res.status}`);
//   const data = await res.json() as { tasks: GeneralTaskRow[]; total: number };
//
// WRITE operations:
//   const res = await apiFetch('/api/v1/blocks/123', {
//     method: 'DELETE',
//   });
//   const res = await apiFetch('/api/v1/blocks', {
//     method: 'POST',
//     body: JSON.stringify(payload),   // Content-Type set automatically
//   });
//
// PATTERN FOR ALL FUTURE MIGRATIONS:
//   1. Create API route in apps/api/src/routes/<feature>.ts
//      - Use verifyCompanyUser() middleware from apps/api/src/lib/auth.ts
//      - Register in apps/api/src/index.ts
//   2. Create/rewrite hook in apps/web/src/hooks/use-<feature>.ts
//      - Import apiFetch from '../lib/api-client'
//      - No direct Supabase imports for data (only for auth.getSession)

import { getCachedToken, getFreshToken, setCachedToken, forceSignOut } from './auth';
import { invalidateBatchCacheForPath } from './batch-cache';

// Production API. For local work against a local API (`pnpm --filter @click2gro/api dev`,
// port 8787) set VITE_API_URL=http://localhost:8787 in apps/web/.env.local.
// Only honoured in dev builds, so a stray env var can never repoint production.
const devApiUrl: unknown = import.meta.env.DEV ? import.meta.env['VITE_API_URL'] : undefined;
export const API_BASE_URL = typeof devApiUrl === 'string' && devApiUrl !== '' ? devApiUrl : 'https://api.click2gro.com.au';

// Mirrors COMPANY_NOT_SELECTED_STATUS in apps/api/src/lib/auth.ts.
const COMPANY_NOT_SELECTED_STATUS = 428;

// 428 means exactly one thing: a superadmin hit a tenant route with no company
// selected. Their session is perfectly valid — they just need to pick a company
// — so send them to the picker instead of signing them out, which is what every
// other 403 means. It has its own status because routes render error bodies
// inconsistently and there is nothing parseable to key off.
function redirectToCompanyPicker(): never {
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/select-company')) {
    window.location.replace('/select-company');
  }
  throw new Error('Select a company to view before using this screen.');
}

export interface ApiFetchOptions {
  /**
   * Return the 401/403 response instead of tearing the session down.
   *
   * The default teardown (clear storage → forceSignOut → hard redirect to
   * /login) is correct for a background poll: the session is gone, so get the
   * user out. It is destructive for a long-lived form — a user who has spent
   * ten minutes on an observation and whose cached token went stale while the
   * camera app was open loses the whole entry to a redirect.
   *
   * Callers that set this own the failure: surface the error next to the
   * control the user pressed and let them retry. Only worth setting on
   * user-initiated writes from a form that holds unsaved state.
   */
  readonly suppressAuthTeardown?: boolean;
}

/**
 * Make an authenticated request to the Click2Gro API worker.
 * Automatically attaches the current session JWT as a Bearer token.
 * Content-Type: application/json is set automatically when body is provided.
 * Throws if the user is not authenticated (no active session).
 *
 * If the first request returns 401 (stale cached token), a fresh token is fetched
 * from Supabase and the request is retried once before giving up.
 */
export async function apiFetch(
  path: string,
  options: RequestInit = {},
  apiOptions: ApiFetchOptions = {},
): Promise<Response> {
  let token = getCachedToken();
  if (!token) throw new Error('Not authenticated');

  const hasBody = options.body !== undefined;
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const buildHeaders = (t: string): Record<string, string> => ({
    ...(options.headers as Record<string, string> ?? {}),
    Authorization: `Bearer ${t}`,
    ...(hasBody && !isFormData ? { 'Content-Type': 'application/json' } : {}),
  });

  let response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: buildHeaders(token),
  });

  // If the cached token was stale, refresh and retry exactly once.
  if (response.status === 401) {
    const freshToken = await getFreshToken();
    if (freshToken && freshToken !== token) {
      setCachedToken(freshToken);
      token = freshToken;
      response = await fetch(`${API_BASE_URL}${path}`, {
        ...options,
        headers: buildHeaders(token),
      });
    }
  }

  if (response.status === COMPANY_NOT_SELECTED_STATUS) redirectToCompanyPicker();

  // If still 401/403 after retry, the session is genuinely invalid — sign out,
  // unless the caller has opted to handle it in place.
  if (response.status === 401 || response.status === 403) {
    if (apiOptions.suppressAuthTeardown) return response;
    tearDownSession();
    throw new Error(
      response.status === 403
        ? 'Access denied: No company associated with this account.'
        : 'Session expired. Please log in again.',
    );
  }

  // A successful write to a stock-bearing endpoint makes every cached batch
  // list stale. Invalidating here (rather than at each call site) is why stock
  // added in one screen shows up in the spray task's batch dropdown next door.
  if (response.ok) invalidateBatchCacheForPath(path, options.method);

  return response;
}

/**
 * Clear the session and get the user to /login.
 *
 * Guarded by a latch: many hooks re-poll concurrently and would each call
 * forceSignOut + redirect, racing against the navigation.
 */
function tearDownSession(): void {
  if (_signedOutLatch) return;
  _signedOutLatch = true;
  // Clear Supabase persisted session synchronously BEFORE the redirect.
  // The async signOut may not finish before we navigate, and if the
  // session is still in localStorage, the login page sees `user` and
  // bounces straight back to /dashboard → API 401s again → infinite loop.
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
        localStorage.removeItem(key);
      }
    }
  } catch { /* localStorage unavailable — fall through */ }
  setCachedToken(null);
  forceSignOut();
  // Hard redirect — React's signOut chain can stall mid-render when
  // multiple hooks throw at once, leaving the user on a broken page
  // where the logout button doesn't respond. Bypass it.
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    window.location.replace('/login');
  }
}

// Module-level latch: once a 401/403 has triggered a forced sign-out, swallow
// further sign-out calls until a fresh page load resets the module. Prevents
// the redirect-vs-react-render race when many concurrent requests fail.
let _signedOutLatch = false;
